import { getSpacing, type MantineSpacing, type PopoverProps } from "@mantine/core";

/** Every menu item's padding, on all four sides. A menu's height in rows is worked out from it (see AutoMenu). */
export const MENU_ITEM_PADDING: MantineSpacing = "md";

/**
 * The height of a menu item holding one line of text: the item padding above and below a line of the item's text. A
 * control row (Menu.Control) keeps to it whatever the size of its control, so it lines up with the other rows.
 */
export const MENU_ITEM_HEIGHT = `calc(${getSpacing(MENU_ITEM_PADDING)} * 2 + var(--mantine-font-size-sm) * var(--mantine-line-height))`;

/** A control row's right padding. It is smaller than an item's, so the control sits a little closer to the edge. */
export const MENU_CONTROL_RIGHT_PADDING: MantineSpacing = "sm";

/** Inside a control row, the selected option of a group (the picked theme, say), which takes focus first. */
export const MENU_CONTROL_SELECTED_SELECTOR = "input:checked:not(:disabled)";

/** Inside a control row, anything that can take focus, used when the control has no selected option. */
export const MENU_CONTROL_FOCUSABLE_SELECTOR =
    'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * The gap, in pixels, between a menu's target and its dropdown. Mantine's `offset` is a pixel number handed to Floating
 * UI, so it cannot take a spacing token such as "xs".
 */
export const MENU_DROPDOWN_OFFSET = 4;

/**
 * Positioning middlewares for a sub-menu.
 *
 * Mantine's `Menu.Sub` ships `shift: { crossAxis: true }`. Floating UI's cross axis is the one that runs along
 * the *side* of the floating element, so for the `right-start` placement a sub-menu uses, that option lets the
 * sub-menu slide horizontally when it does not fit beside its parent. Because Mantine registers `shift` before
 * `flip` (`@mantine/core/esm/components/Popover/use-popover.mjs`), the slide resolves the overflow first and
 * `flip` then never fires — the same ordering trap already documented in `popover.constants.ts`.
 *
 * The result is that a sub-menu wider than the room to the right of its parent is dragged back on top of that
 * parent, covering the items the user still has to choose from, instead of opening to the left where there is
 * room. It only shows up on wide sub-menus, which is why most of them look fine: the widest item sets the
 * dropdown's width, and `menu.module.scss` lets an item's label run to 400px.
 *
 * Turning the cross-axis slide off leaves the side choice to `flip`, which is what picks the roomier side.
 * The main axis stays enabled, so a tall sub-menu still slides vertically to stay in view.
 *
 * If the sub-menu fits on neither side, `flip` falls back to the side that overflows least and the sub-menu
 * overhangs the viewport, exactly as any other Mantine overlay does. That is more recoverable than covering
 * the menu that opened it, which leaves the user nothing to click.
 */
export const SUB_MENU_FIT_MIDDLEWARES: NonNullable<PopoverProps["middlewares"]> = {
    shift: { crossAxis: false },
};
